/**
 * InlineDdlGrantBody — the embeddable content (parked-statement scan +
 * `GrantEditFormBody`) for parked GRANT / REVOKE statements, consumed
 * directly by the Compact DDL editor's `compactDdlBodyArm`.
 *
 * `GrantEditFormBody` is seeded from the grammar-backed DSE2
 * `extractParkedGrant` helper (VDE-2 Slice 1; replaces the removed regex
 * parked-grant parser).
 *
 * `InlineDdlGrantBody` swaps in for `InlineDdlSecurityBody` whenever the
 * cursor is parked in a GRANT / REVOKE statement (`ddlKind: 'grant' |
 * 'revoke'`). Both share `objectKind: 'security'` so the same
 * `compactDdlBodyArm` `'security'` arm drives both; the dispatcher picks on
 * `ddlKind`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice F2.
 */
import { useMemo } from 'react';

import type { VqeDdlEditContext } from '../edit/editContext';
import { GrantEditFormBody } from '../render/common/GrantEditFormBody';
import {
  valuesFromParsedGrant,
  type GrantFormValues,
} from '../render/common/grantFormHelpers';
import {
  extractParkedGrant,
  type ParsedParkedGrant,
} from '../../../sql-resolver/ddl/extract/grant';

export interface InlineDdlGrantBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-grant statement. */
  readonly parsedOverride?: ParsedParkedGrant | null;
  /** Invoked after a successful commit or cancel, so an embedding popover can close. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

export function InlineDdlGrantBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlGrantBodyProps) {
  const parsed = useMemo<ParsedParkedGrant | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      return extractParkedGrant(buf, stmt);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);

  const initialValues: Partial<GrantFormValues> | undefined = parsed
    ? valuesFromParsedGrant(parsed)
    : undefined;

  return (
    <GrantEditFormBody
      editContext={editContext}
      initialValues={initialValues}
      testId="inline-ddl-grant-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
